/**
 * Financial Worksheet Panel — displays period-indexed financial data as
 * tables with optional inline charts, toggled by the user. Each chart shares
 * its table's column geometry and scroll region (MOR-1282); the detailed
 * income statement and balance sheet open in place beneath the analysis they
 * support (MOR-1281).
 */

import {
  Fragment,
  createContext,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  GRANULARITIES,
  displayPeriodLabel,
  pickDefaultGranularity,
  type Granularity,
} from "./granularity";
import { Button } from "@/ui/primitives/button";
import {
  Loader2,
  Table2,
  FileText,
  BarChart3,
  TableProperties,
  Link2,
  Calculator,
  Plus,
  ChevronRight,
  Info,
  AlertTriangle,
  Trash2,
} from "lucide-react";
import { cn } from "@/utils/misc";
import { ConvexError } from "convex/values";
import { toast } from "sonner";
import {
  JobChatDocumentPicker,
  type ChatDocPick,
} from "@/components/agents/universal/ChatDocumentPicker";
import { ScrollArea, ScrollBar } from "@/ui/primitives/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/composed/overlays/tooltip";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/ui/composed/overlays/alert-dialog";
import { FinancialPeriodDetailPanel } from "./FinancialPeriodDetailPanel";
import { EvidenceFocusBanner } from "./EvidenceFocusBanner";
import {
  findEvidencePeriod,
  resolveInitialGranularity,
} from "./financialContext";
import { StatementDisclosure } from "./StatementDisclosure";
import { METRIC_COLUMN_PX, periodRegionMinWidth } from "./periodLayout";
import type { EvidenceFocus, FinancialPeriodEntryUI } from "./types";

// Shared chart components
import { ProfitLossChart } from "@/components/jobs/charts/ProfitLossChart";
import { NetAssetsChart } from "@/components/jobs/charts/NetAssetsChart";
import { CurrentRatioChart } from "@/components/jobs/charts/CurrentRatioChart";
import { CashFlowChart } from "@/components/jobs/charts/CashFlowChart";
import { StatutoryLiabilitiesChart } from "@/components/jobs/charts/StatutoryLiabilitiesChart";
import { AgedPayablesChart } from "@/components/jobs/charts/AgedPayablesChart";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FinancialWorksheetPanelProps {
  jobId: Id<"jobs">;
  taskId: Id<"summarizationTasks">;
  isRunning: boolean;
  onViewDocument?: (docId: string) => void;
  isViewer?: boolean;
  /**
   * Granularity carried in the route (MOR-1280) — opened on when it has
   * periods, instead of the coarsest one.
   */
  preferredGranularity?: Granularity;
  /** Reports the practitioner's granularity choice back to the route. */
  onGranularityChange?: (granularity: Granularity) => void;
  /** Arrival from an indicator's supporting financials (MOR-1280). */
  evidenceFocus?: EvidenceFocus;
}

/**
 * Row shape returned by `listFinancialPeriodEntriesForJob`. The query still
 * declares `returns: v.array(v.any())`, so the rows are typed here at the
 * boundary: the stored `FinancialPeriodEntryUI` shape plus the computed
 * overlay fields the handler adds — `adjustedFields` (MOR-810 practitioner
 * adjustments), and `rbaFields` / `recordedTaxLiabilities` (MOR-859 ATO RBA
 * statutory source).
 */
interface WorksheetPeriodEntry extends FinancialPeriodEntryUI {
  rbaFields?: string[];
  adjustedFields?: string[];
  recordedTaxLiabilities?: number;
}

/**
 * documentId → display title (documentType, falling back to fileName).
 * Context rather than props because source-doc chips render several
 * components deep (BlockTable) and everything in between would otherwise
 * need the map threaded through.
 */
const DocTitleContext = createContext<Record<string, string>>({});

/**
 * Period an indicator's supporting evidence cites (MOR-1280), highlighted in
 * every period table. Context for the same reason as DocTitleContext.
 */
const HighlightPeriodContext = createContext<string | null>(null);

function useDocTitle() {
  const titles = useContext(DocTitleContext);
  return (doc: { documentId: string; fileName: string }) =>
    titles[doc.documentId] ?? doc.fileName;
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

function formatValue(value: number | null | undefined, format: string): string {
  if (value == null) return "—";
  if (format === "ratio") return value.toFixed(2);
  if (format === "percentage") return `${(value * 100).toFixed(1)}%`;
  const abs = Math.abs(value);
  const formatted =
    abs >= 1_000_000
      ? `$${(abs / 1_000_000).toFixed(1)}M`
      : abs >= 1_000
        ? `$${(abs / 1_000).toFixed(0)}K`
        : `$${abs.toLocaleString()}`;
  return value < 0 ? `(${formatted})` : formatted;
}

function extractArray(
  periods: WorksheetPeriodEntry[],
  field: string,
): (number | null)[] {
  return periods.map((p) => {
    const value = p[field];
    return typeof value === "number" ? value : null;
  });
}

function computeCumulative(values: (number | null)[]): (number | null)[] {
  let sum = 0;
  return values.map((v) => {
    if (v == null) return null;
    sum += v;
    return sum;
  });
}

function hasAny(periods: WorksheetPeriodEntry[], ...fields: string[]): boolean {
  return fields.some((f) => periods.some((p) => p[f] != null));
}

// ---------------------------------------------------------------------------
// Detailed statements — revealed in place beneath the analysis they support
// (MOR-1281) rather than sitting at the bottom of the worksheet.
// ---------------------------------------------------------------------------

const INCOME_STATEMENT_ROWS: BlockRow[] = [
  { label: "Revenue", field: "revenue", format: "currency" },
  {
    label: "Cost of Goods Sold",
    field: "costOfGoodsSold",
    format: "currency",
  },
  {
    label: "Gross Profit",
    field: "grossProfit",
    format: "currency",
    isBold: true,
  },
  {
    label: "Operating Expenses",
    field: "operatingExpenses",
    format: "currency",
  },
  { label: "EBITDA", field: "ebitda", format: "currency" },
  {
    label: "D&A",
    field: "depreciationAmortisation",
    format: "currency",
  },
  { label: "EBIT", field: "ebit", format: "currency" },
  {
    label: "Interest Expense",
    field: "interestExpense",
    format: "currency",
  },
  {
    label: "Profit Before Tax",
    field: "netProfitBeforeTax",
    format: "currency",
  },
  { label: "Income Tax", field: "incomeTax", format: "currency" },
  {
    label: "Net Profit After Tax",
    field: "netProfitAfterTax",
    format: "currency",
    isBold: true,
  },
];

/** Fields whose presence means there is an income statement to show. */
const INCOME_STATEMENT_KEY_FIELDS = [
  "revenue",
  "costOfGoodsSold",
  "grossProfit",
  "operatingExpenses",
];

const BALANCE_SHEET_ROWS: BlockRow[] = [
  {
    label: "Cash & Equivalents",
    field: "cashAndEquivalents",
    format: "currency",
  },
  {
    label: "Trade Receivables",
    field: "tradeReceivables",
    format: "currency",
  },
  { label: "Inventory", field: "inventory", format: "currency" },
  {
    label: "Total Current Assets",
    field: "totalCurrentAssets",
    format: "currency",
    isBold: true,
  },
  {
    label: "PP&E",
    field: "propertyPlantEquipment",
    format: "currency",
  },
  {
    label: "Intangible Assets",
    field: "intangibleAssets",
    format: "currency",
  },
  {
    label: "Total Non-Current Assets",
    field: "totalNonCurrentAssets",
    format: "currency",
    isBold: true,
  },
  {
    label: "Total Assets",
    field: "totalAssets",
    format: "currency",
    isBold: true,
  },
  {
    label: "Trade Payables",
    field: "tradePayables",
    format: "currency",
  },
  {
    label: "Short-Term Borrowings",
    field: "shortTermBorrowings",
    format: "currency",
  },
  {
    label: "Tax Liabilities",
    field: "taxLiabilities",
    format: "currency",
    hint: "Statutory tax owing (ATO ICA, GST, PAYG). Part of current liabilities — the stated Total Current Liabilities from the source document normally already includes it, so the two rows are not additive.",
  },
  {
    label: "Super Liabilities",
    field: "superannuationLiabilities",
    format: "currency",
  },
  {
    label: "Other Current Liabilities",
    field: "otherCurrentLiabilities",
    format: "currency",
  },
  {
    label: "Total Current Liabilities",
    field: "totalCurrentLiabilities",
    format: "currency",
    isBold: true,
  },
  {
    label: "Long-Term Borrowings",
    field: "longTermBorrowings",
    format: "currency",
  },
  {
    label: "Total Non-Current Liabilities",
    field: "totalNonCurrentLiabilities",
    format: "currency",
    isBold: true,
  },
  {
    label: "Total Liabilities",
    field: "totalLiabilities",
    format: "currency",
    isBold: true,
  },
  {
    label: "Net Assets",
    field: "netAssets",
    format: "currency",
    isBold: true,
  },
  {
    label: "Retained Earnings",
    field: "retainedEarnings",
    format: "currency",
  },
  {
    label: "Total Equity",
    field: "totalEquity",
    format: "currency",
    isBold: true,
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Extracted financial statements for a job by granularity, with charts
 * aligned to the tables (MOR-1282). Starts on `preferredGranularity` or the
 * granularity holding the evidence period, reports granularity changes to
 * the parent, and highlights the `evidenceFocus` period when set.
 */
export function FinancialWorksheetPanel({
  jobId,
  taskId,
  isRunning,
  onViewDocument,
  isViewer,
  preferredGranularity,
  onGranularityChange,
  evidenceFocus,
}: FinancialWorksheetPanelProps) {
  const [showCharts, setShowCharts] = useState(true);
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);

  const addPeriod = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.financialAnalysis.publicMutations
        .addManualFinancialPeriod,
    ),
  });

  // MOR-859 — recorded (financials) vs actual (ATO RBA) statutory liabilities.
  // MOR-810 — original vs practitioner-adjusted basis.
  const { data: statSettings } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.getFinancialAnalysisSettings,
      { jobId },
    ),
  );
  const basis = statSettings?.analysisBasis ?? "original";
  const setAnalysisBasis = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.financialAnalysis.publicMutations
        .setAnalysisBasis,
    ),
  });

  const { data: entriesData } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.listFinancialPeriodEntriesForJob,
      { jobId, basis },
    ),
  );
  // The query still declares `returns: v.array(v.any())`, so its rows are
  // typed once here at the boundary (see WorksheetPeriodEntry above).
  const entries: WorksheetPeriodEntry[] | undefined = entriesData;
  const { data: atoLedger } = useQuery(
    convexQuery(api.jobs.summarization.publicQueries.listAtoLedgerForJob, {
      jobId,
    }),
  );
  const setStatutorySource = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.financialAnalysis.publicMutations
        .setStatutoryLiabilitySource,
    ),
  });
  // Balance the ATO says was owed as at a date: per authority, the last
  // stamped movement on or before it; null before the ledger's coverage.
  const rbaBalanceAt = useMemo(() => {
    interface LedgerMovement {
      kind?: string;
      effectiveDate: string;
      rebuiltBalance?: number;
      atoSroEntryId?: string;
    }
    const movements = ((atoLedger ?? []) as Array<Partial<LedgerMovement>>)
      .filter(
        (r): r is LedgerMovement =>
          r.kind === "movement" && typeof r.effectiveDate === "string",
      )
      .sort((a, b) => a.effectiveDate.localeCompare(b.effectiveDate));
    if (movements.length === 0) return null;
    const firstDate = movements[0].effectiveDate;
    return (date?: string): number | null => {
      if (!date || date < firstDate) return null;
      const lastByEntry = new Map<string, number>();
      for (const m of movements) {
        if (m.effectiveDate > date) break;
        lastByEntry.set(String(m.atoSroEntryId), m.rebuiltBalance ?? 0);
      }
      if (lastByEntry.size === 0) return null;
      const total = Array.from(lastByEntry.values()).reduce(
        (sum, b) => sum + b,
        0,
      );
      return Math.round(total * 100) / 100;
    };
  }, [atoLedger]);

  const grouped = useMemo(() => {
    if (!entries) return { annual: [], quarterly: [], monthly: [], weekly: [] };
    const active = entries.filter((e) => e.status === "active");
    const sort = (arr: WorksheetPeriodEntry[]) =>
      [...arr].sort((a, b) =>
        (a.endDate ?? a.periodLabel).localeCompare(b.endDate ?? b.periodLabel),
      );
    return {
      annual: sort(active.filter((e) => e.periodType === "annual")),
      quarterly: sort(active.filter((e) => e.periodType === "quarterly")),
      monthly: sort(active.filter((e) => e.periodType === "monthly")),
      weekly: sort(active.filter((e) => e.periodType === "weekly")),
    };
  }, [entries]);

  const counts: Record<Granularity, number> = useMemo(
    () => ({
      annual: grouped.annual.length,
      quarterly: grouped.quarterly.length,
      monthly: grouped.monthly.length,
      weekly: grouped.weekly.length,
    }),
    [grouped],
  );
  // The period an indicator's supporting evidence cites (MOR-1280) — null
  // when it names a period that was not extracted.
  const evidencePeriod = useMemo(
    () => findEvidencePeriod(entries ?? [], evidenceFocus?.periodLabel),
    [entries, evidenceFocus?.periodLabel],
  );
  const evidencePeriodType = evidencePeriod?.periodType;
  const [granularity, setGranularity] = useState<Granularity>(
    preferredGranularity ?? "annual",
  );
  // Carried choice → evidence period's granularity → coarsest with data.
  useEffect(() => {
    setGranularity(
      resolveInitialGranularity(
        counts,
        preferredGranularity,
        evidencePeriodType,
        pickDefaultGranularity(counts),
      ),
    );
  }, [counts, preferredGranularity, evidencePeriodType]);
  /** Switches granularity locally and reports it to the route context. */
  const changeGranularity = (next: Granularity) => {
    setGranularity(next);
    onGranularityChange?.(next);
  };

  const { data: jobDocs } = useQuery(
    convexQuery(api.jobs.gatherInformation.documents.queries.listDocuments, {
      jobId,
    }),
  );
  const docTitles = useMemo(() => {
    const map: Record<string, string> = {};
    for (const d of jobDocs ?? []) {
      if (d.documentType) map[d._id as string] = d.documentType;
    }
    return map;
  }, [jobDocs]);

  const [showSourceDocs, setShowSourceDocs] = useState(false);
  const allSourceDocs = useMemo(() => {
    const docMap = new Map<string, { documentId: string; fileName: string }>();
    // Active rows only — a removed / merged period must not keep its
    // documents in the analysis list (MOR-955).
    for (const e of (entries ?? []).filter((e) => e.status === "active")) {
      for (const doc of e.sourceDocuments ?? []) {
        if (!docMap.has(doc.documentId)) docMap.set(doc.documentId, doc);
      }
    }
    return Array.from(docMap.values()).sort((a, b) =>
      (docTitles[a.documentId] ?? a.fileName).localeCompare(
        docTitles[b.documentId] ?? b.fileName,
      ),
    );
  }, [entries, docTitles]);

  // MOR-955 — remove / add source documents.
  const [removeDocTarget, setRemoveDocTarget] = useState<{
    documentId: string;
    fileName: string;
  } | null>(null);
  const [addDocOpen, setAddDocOpen] = useState(false);
  const removeDocument = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.financialAnalysis.publicMutations
        .removeDocumentFromFinancialAnalysis,
    ),
  });
  const addDocument = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.financialAnalysis.publicMutations
        .addDocumentToFinancialAnalysis,
    ),
  });
  const handleRemoveDoc = () => {
    if (!removeDocTarget) return;
    removeDocument.mutate(
      { jobId, documentId: removeDocTarget.documentId },
      {
        onSuccess: (result) => {
          // Say what actually happened — figures a person typed are kept, so
          // a blanket "removed" would misrepresent the result (MOR-955).
          const kept =
            result.valuesKept > 0
              ? ` ${result.valuesKept} manually edited figure${result.valuesKept === 1 ? "" : "s"} kept.`
              : "";
          toast.success(
            `Document removed — ${result.fieldsCleared} figure${result.fieldsCleared === 1 ? "" : "s"} updated across ${result.periodsUpdated} period${result.periodsUpdated === 1 ? "" : "s"}` +
              (result.periodsRemoved > 0
                ? `, ${result.periodsRemoved} period${result.periodsRemoved === 1 ? "" : "s"} removed.`
                : ".") +
              kept,
          );
          setRemoveDocTarget(null);
        },
        onError: (err) => {
          toast.error(
            err instanceof ConvexError && typeof err.data === "string"
              ? err.data
              : "Failed to remove document",
          );
        },
      },
    );
  };

  const handleAddDocs = async (picked: ChatDocPick[]) => {
    setAddDocOpen(false);
    if (picked.length === 0) return;
    let added = 0;
    let extracting = 0;
    for (const pick of picked) {
      try {
        const result = await addDocument.mutateAsync({
          jobId,
          documentId: pick.documentId,
        });
        added++;
        if (result.needsExtraction) extracting++;
      } catch (err) {
        toast.error(
          err instanceof ConvexError && typeof err.data === "string"
            ? err.data
            : `Failed to add "${pick.fileName}"`,
        );
      }
    }
    if (added === 0) return;
    toast.success(
      `${added} document${added === 1 ? "" : "s"} added to the analysis` +
        (extracting > 0
          ? ` — ${extracting === added ? "reading" : `${extracting} needs reading`} the financials first, so the figures take a moment to appear.`
          : " — figures will update shortly."),
    );
  };

  // Rendered both in the toolbar and in the all-periods-removed empty state.
  // Any library document can be picked, not just the ones the classifier
  // filed as financial — a misclassified balance sheet is exactly what needs
  // adding by hand, and the backend runs the extraction it is missing.
  const addDocumentButton = (
    <button
      type="button"
      onClick={() => setAddDocOpen(true)}
      className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-700 transition-colors hover:bg-violet-100 dark:border-violet-800 dark:bg-violet-950 dark:text-violet-300 dark:hover:bg-violet-900"
    >
      <Plus className="h-3 w-3 shrink-0" />
      Add document
    </button>
  );

  const selectedEntry = useMemo(
    () => (entries ?? []).find((e) => e._id === selectedEntryId) ?? null,
    [entries, selectedEntryId],
  );

  if (!entries) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-emerald-500" />
      </div>
    );
  }

  const totalActive =
    grouped.annual.length +
    grouped.quarterly.length +
    grouped.monthly.length +
    grouped.weekly.length;
  const periods = grouped[granularity];

  if (totalActive === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-12">
        {isRunning ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin text-emerald-500" />
            <p className="text-sm text-muted-foreground">
              Extracting financial data...
            </p>
          </>
        ) : (
          <>
            <Table2 className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              No financial periods extracted yet.
            </p>
            {!isViewer && addDocumentButton}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-0.5 rounded-md border border-border p-0.5">
            {GRANULARITIES.map(({ key, label }) => {
              const n = counts[key];
              const selected = granularity === key;
              return (
                <button
                  key={key}
                  type="button"
                  disabled={n === 0}
                  onClick={() => changeGranularity(key)}
                  className={cn(
                    "rounded px-2.5 py-1 text-xs transition-colors",
                    selected
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground",
                    n === 0
                      ? "cursor-not-allowed opacity-40"
                      : "hover:bg-muted",
                  )}
                >
                  {label}
                  {n > 0 ? ` (${n})` : ""}
                </button>
              );
            })}
          </div>
          {onViewDocument && allSourceDocs.length > 0 && (
            <>
              <div className="mx-1 h-4 w-px bg-border" />
              <button
                type="button"
                onClick={() => setShowSourceDocs((v) => !v)}
                className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-700 transition-colors hover:bg-violet-100 dark:border-violet-800 dark:bg-violet-950 dark:text-violet-300 dark:hover:bg-violet-900"
              >
                <FileText className="h-3 w-3 shrink-0" />
                Documents ({allSourceDocs.length})
                <ChevronRight
                  className={cn(
                    "h-3 w-3 shrink-0 transition-transform",
                    showSourceDocs && "rotate-90",
                  )}
                />
              </button>
            </>
          )}
          {!isViewer && (
            <>
              <div className="mx-1 h-4 w-px bg-border" />
              {addDocumentButton}
              <Button
                variant="outline"
                size="sm"
                className="h-6 gap-1 px-2 text-[10px]"
                disabled={addPeriod.isPending}
                onClick={() => {
                  addPeriod.mutate(
                    {
                      jobId,
                      taskId,
                      periodLabel: "New Period",
                      periodType: "annual" as const,
                    },
                    { onSuccess: (id) => setSelectedEntryId(id as string) },
                  );
                }}
              >
                <Plus className="h-3 w-3" />
                Add Period
              </Button>
            </>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Original vs practitioner-adjusted basis (MOR-810). The active mode
            labels the whole analysis; everything below renders on it. */}
          <div className="flex items-center gap-1 rounded-lg border bg-muted/30 p-0.5">
            {(
              [
                { key: "original", label: "Original" },
                { key: "adjusted", label: "Adjusted" },
              ] as const
            ).map(({ key, label }) => {
              const button = (
                <Button
                  variant={basis === key ? "default" : "ghost"}
                  size="sm"
                  disabled={isViewer && basis !== key}
                  className={cn(
                    "h-7 px-2.5 text-xs",
                    basis === key &&
                      key === "adjusted" &&
                      "bg-amber-600 text-white hover:bg-amber-600/90",
                  )}
                  onClick={() =>
                    !isViewer && setAnalysisBasis.mutate({ jobId, basis: key })
                  }
                >
                  {label}
                </Button>
              );

              if (key !== "adjusted" || basis !== "adjusted") {
                return <Fragment key={key}>{button}</Fragment>;
              }

              return (
                <TooltipProvider key={key} delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>{button}</TooltipTrigger>
                    <TooltipContent className="max-w-xs text-xs leading-relaxed">
                      Practitioner adjustments applied
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            })}
          </div>
          <div className="flex items-center gap-1 rounded-lg border bg-muted/30 p-0.5">
            <Button
              variant={showCharts ? "default" : "ghost"}
              size="sm"
              className="h-7 gap-1.5 px-2.5 text-xs"
              onClick={() => setShowCharts(true)}
            >
              <BarChart3 className="h-3.5 w-3.5" />
              Charts
            </Button>
            <Button
              variant={!showCharts ? "default" : "ghost"}
              size="sm"
              className="h-7 gap-1.5 px-2.5 text-xs"
              onClick={() => setShowCharts(false)}
            >
              <TableProperties className="h-3.5 w-3.5" />
              Tables only
            </Button>
          </div>
        </div>
      </div>

      {/* Source documents — expanded table */}
      {onViewDocument && showSourceDocs && allSourceDocs.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-violet-200 bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] dark:border-violet-800">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-violet-50 text-violet-700 dark:bg-violet-950 dark:text-violet-300">
                <th className="px-3 py-1.5 text-left font-medium">Document</th>
                <th className="hidden w-56 px-3 py-1.5 text-left font-medium md:table-cell">
                  File
                </th>
                {!isViewer && <th className="w-8 px-2 py-1.5" />}
              </tr>
            </thead>
            <tbody>
              {allSourceDocs.map((doc) => (
                <tr
                  key={doc.documentId}
                  onClick={() => onViewDocument(doc.documentId)}
                  className="cursor-pointer border-t border-border/60 bg-background transition-colors even:bg-muted/40 hover:bg-violet-50/70 dark:hover:bg-violet-950/30"
                >
                  <td className="px-3 py-1.5">
                    <span className="inline-flex items-center gap-1.5">
                      <FileText className="h-3 w-3 shrink-0 text-violet-500 dark:text-violet-400" />
                      {docTitles[doc.documentId] ?? doc.fileName}
                    </span>
                  </td>
                  <td className="hidden max-w-56 truncate px-3 py-1.5 text-muted-foreground md:table-cell">
                    {doc.fileName}
                  </td>
                  {!isViewer && (
                    <td className="w-8 px-2 py-1.5 text-right">
                      <button
                        type="button"
                        title="Remove from analysis"
                        onClick={(e) => {
                          e.stopPropagation();
                          setRemoveDocTarget(doc);
                        }}
                        className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-red-600 dark:hover:text-red-400"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Unallocated line items — accounts no classifier could place with
          confidence. Surfaced instead of silently bucketed (MOR-857); the
          period panel has the allocate action. */}
      {(() => {
        const withUnallocated = (entries ?? []).filter(
          (e) => e.status === "active" && (e.unallocatedItems?.length ?? 0) > 0,
        );
        if (withUnallocated.length === 0) return null;
        return (
          <div className="rounded-lg border border-amber-200 bg-amber-50/50 px-4 py-3 dark:border-amber-900 dark:bg-amber-950/20">
            <p className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 dark:text-amber-400">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              Unallocated line items
            </p>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              These source lines could not be matched to a standard category
              with confidence, so they are NOT included in any figure below.
              Open the period to allocate them.
            </p>
            <ul className="mt-2 space-y-1">
              {withUnallocated.map((e) => (
                <li key={e._id} className="text-xs">
                  <button
                    type="button"
                    onClick={() => setSelectedEntryId(e._id)}
                    className="font-medium underline decoration-dotted hover:text-amber-700 dark:hover:text-amber-400"
                  >
                    {e.periodLabel}
                  </button>
                  <span className="text-muted-foreground">
                    {" — "}
                    {(e.unallocatedItems ?? [])
                      .map(
                        (u) =>
                          `${u.accountName} ($${Math.round(u.balance).toLocaleString()})`,
                      )
                      .join(", ")}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        );
      })()}

      {evidenceFocus && (
        <EvidenceFocusBanner
          focus={evidenceFocus}
          periodFound={evidencePeriod !== null}
          periodGranularityLabel={
            evidencePeriod && evidencePeriod.periodType !== granularity
              ? GRANULARITIES.find((g) => g.key === evidencePeriod.periodType)
                  ?.label
              : undefined
          }
          onShowPeriod={
            evidencePeriod
              ? () => changeGranularity(evidencePeriod.periodType)
              : undefined
          }
        />
      )}

      {periods.length > 0 ? (
        <DocTitleContext.Provider value={docTitles}>
          <HighlightPeriodContext.Provider value={evidencePeriod?._id ?? null}>
            <PeriodSection
              title={`${GRANULARITIES.find((g) => g.key === granularity)?.label ?? "Annual"} Financial Data`}
              periods={periods}
              showCharts={showCharts}
              onViewDocument={onViewDocument}
              onSelectPeriod={setSelectedEntryId}
              statutorySource={
                statSettings?.statutoryLiabilitySource ?? "financials"
              }
              rbaBalanceAt={rbaBalanceAt}
              onSetStatutorySource={
                isViewer
                  ? undefined
                  : (source) => setStatutorySource.mutate({ jobId, source })
              }
            />
          </HighlightPeriodContext.Provider>
        </DocTitleContext.Provider>
      ) : (
        <div className="px-3 py-8 text-center text-sm text-muted-foreground">
          No {granularity} periods found in this job's documents.
        </div>
      )}

      {/* Remove-document confirmation (MOR-955) */}
      <AlertDialog
        open={removeDocTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRemoveDocTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove document from analysis?</AlertDialogTitle>
            <AlertDialogDescription>
              Values extracted from{" "}
              <span className="font-medium text-foreground">
                {removeDocTarget
                  ? (docTitles[removeDocTarget.documentId] ??
                    removeDocTarget.fileName)
                  : ""}
              </span>{" "}
              will be removed and the solvency indicators recomputed. The
              document stays in the Library and can be added back later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeDocument.isPending}>
              Cancel
            </AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={removeDocument.isPending}
              onClick={handleRemoveDoc}
            >
              {removeDocument.isPending && (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              )}
              Remove
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* The job chat's Finder — the whole library, so a document the
          classifier misfiled can still be pulled into the analysis. */}
      {!isViewer && (
        <JobChatDocumentPicker
          jobId={jobId}
          open={addDocOpen}
          onClose={() => setAddDocOpen(false)}
          onSelect={(picked) => void handleAddDocs(picked)}
        />
      )}

      <FinancialPeriodDetailPanel
        entry={selectedEntry}
        open={selectedEntryId !== null}
        onClose={() => setSelectedEntryId(null)}
        onDeleted={() => setSelectedEntryId(null)}
        onViewDocument={onViewDocument}
        isViewer={isViewer}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Period Section — single column, charts inline after relevant tables
// ---------------------------------------------------------------------------

/**
 * One granularity's statements as a column of block tables, each followed by
 * the chart it supports, with detailed statements behind disclosures.
 */
function PeriodSection({
  title,
  periods,
  showCharts,
  onViewDocument,
  onSelectPeriod,
  statutorySource = "financials",
  rbaBalanceAt,
  onSetStatutorySource,
}: {
  title: string;
  periods: WorksheetPeriodEntry[];
  showCharts: boolean;
  onViewDocument?: (docId: string) => void;
  onSelectPeriod?: (id: string) => void;
  statutorySource?: "financials" | "ato_rba";
  rbaBalanceAt?: ((date?: string) => number | null) | null;
  onSetStatutorySource?: (source: "financials" | "ato_rba") => void;
}) {
  const labels = periods.map((p) => displayPeriodLabel(p));
  const canChart = showCharts && periods.length >= 2;

  // MOR-1281 — the detailed statements open in place beneath the analysis
  // they support. Collapsed by default so the analysis stays scannable; the
  // state survives a granularity switch.
  const idBase = useId();
  const incomeStatementId = `${idBase}income-statement`;
  const balanceSheetId = `${idBase}balance-sheet`;
  const [incomeStatementOpen, setIncomeStatementOpen] = useState(false);
  const [balanceSheetOpen, setBalanceSheetOpen] = useState(false);
  const hasIncomeStatement = hasAny(periods, ...INCOME_STATEMENT_KEY_FIELDS);
  const hasBalanceSheet = hasAny(
    periods,
    ...BALANCE_SHEET_ROWS.map((r) => r.field),
  );

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-shadow duration-200 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)]">
      <div className="px-5 pb-4 pt-5">
        <h4 className="font-sans text-xs font-semibold uppercase tracking-wider text-[#121212]">
          {title}
        </h4>
      </div>

      {/* Profit & Loss */}
      <BlockTable
        title="Continued losses"
        periods={periods}
        onViewDocument={onViewDocument}
        onSelectPeriod={onSelectPeriod}
        rows={[
          {
            label: "Net Profit / (Loss)",
            field: "netProfitAfterTax",
            format: "currency",
            isBold: true,
          },
        ]}
        chart={
          canChart && hasAny(periods, "netProfitAfterTax") ? (
            <ProfitLossChart
              periods={labels}
              plotInsetLeft={METRIC_COLUMN_PX}
              profitLoss={extractArray(periods, "netProfitAfterTax")}
              cumulativeProfitLoss={computeCumulative(
                extractArray(periods, "netProfitAfterTax"),
              )}
            />
          ) : null
        }
      />

      {hasIncomeStatement && (
        <StatementDisclosure
          id={incomeStatementId}
          label="income statement"
          open={incomeStatementOpen}
          onOpenChange={setIncomeStatementOpen}
        >
          <BlockTable
            title="Income statement"
            periods={periods}
            onViewDocument={onViewDocument}
            onSelectPeriod={onSelectPeriod}
            rows={INCOME_STATEMENT_ROWS}
          />
        </StatementDisclosure>
      )}

      {/* Net Asset Analysis */}
      <BlockTable
        title="Net asset analysis"
        periods={periods}
        onViewDocument={onViewDocument}
        onSelectPeriod={onSelectPeriod}
        rows={[
          { label: "Total Assets", field: "totalAssets", format: "currency" },
          {
            label: "Total Liabilities",
            field: "totalLiabilities",
            format: "currency",
          },
          {
            label: "Net Assets",
            field: "netAssets",
            format: "currency",
            isBold: true,
          },
        ]}
        chart={
          canChart && hasAny(periods, "totalAssets", "totalLiabilities") ? (
            <NetAssetsChart
              periods={labels}
              plotInsetLeft={METRIC_COLUMN_PX}
              totalAssets={extractArray(periods, "totalAssets")}
              totalLiabilities={extractArray(periods, "totalLiabilities")}
              netAssets={extractArray(periods, "netAssets")}
            />
          ) : null
        }
      />

      {hasBalanceSheet && (
        <StatementDisclosure
          id={balanceSheetId}
          label="balance sheet"
          open={balanceSheetOpen}
          onOpenChange={setBalanceSheetOpen}
        >
          <BlockTable
            title="Balance sheet"
            periods={periods}
            onViewDocument={onViewDocument}
            onSelectPeriod={onSelectPeriod}
            rows={BALANCE_SHEET_ROWS}
          />
        </StatementDisclosure>
      )}

      {/* Short Term Debt + Liquidity */}
      <BlockTable
        title="Short term debt & liquidity"
        periods={periods}
        onViewDocument={onViewDocument}
        onSelectPeriod={onSelectPeriod}
        rows={[
          {
            label: "Current Assets",
            field: "totalCurrentAssets",
            format: "currency",
          },
          {
            label: "Current Liabilities",
            field: "totalCurrentLiabilities",
            format: "currency",
          },
          {
            label: "Working Capital",
            field: "workingCapital",
            format: "currency",
            isBold: true,
          },
          {
            label: "Current Ratio",
            field: "currentRatio",
            format: "ratio",
            hint: "Current ratio — current assets ÷ current liabilities. Measures whether the company could meet its short-term obligations from its short-term assets. A ratio below 1.0 means current liabilities exceed current assets, a common indicator of insolvency but not determinative on its own. Current assets are taken at book value; inventory and debtors may realise for materially less in a distressed scenario.",
          },
          {
            label: "Debt Ratio",
            field: "debtRatio",
            format: "ratio",
            hint: "Debt ratio — total liabilities ÷ total assets. Above 1.0 the company's liabilities exceed its assets at book value.",
          },
        ]}
        chart={
          canChart && hasAny(periods, "currentRatio", "debtRatio") ? (
            <CurrentRatioChart
              periods={labels}
              plotInsetLeft={METRIC_COLUMN_PX}
              currentRatio={extractArray(periods, "currentRatio")}
              debtRatio={extractArray(periods, "debtRatio")}
            />
          ) : null
        }
      />

      {/* Cash Flow */}
      {hasAny(periods, "operatingCashFlow", "netCashFlow") && (
        <>
          <BlockTable
            title="Cash flow"
            periods={periods}
            onViewDocument={onViewDocument}
            onSelectPeriod={onSelectPeriod}
            rows={[
              {
                label: "Operating Cash Flow",
                field: "operatingCashFlow",
                format: "currency",
              },
              {
                label: "Investing Cash Flow",
                field: "investingCashFlow",
                format: "currency",
              },
              {
                label: "Financing Cash Flow",
                field: "financingCashFlow",
                format: "currency",
              },
              {
                label: "Net Cash Change",
                field: "netCashFlow",
                format: "currency",
                isBold: true,
              },
            ]}
            chart={
              canChart ? (
                <CashFlowChart
                  periods={labels}
                  plotInsetLeft={METRIC_COLUMN_PX}
                  netCashChange={extractArray(periods, "netCashFlow")}
                  cumulativeCashChange={computeCumulative(
                    extractArray(periods, "netCashFlow"),
                  )}
                />
              ) : null
            }
          />
        </>
      )}

      {/* Statutory Liabilities */}
      {hasAny(periods, "taxLiabilities", "superannuationLiabilities") && (
        <>
          <BlockTable
            title="Statutory liabilities"
            periods={periods}
            onViewDocument={onViewDocument}
            onSelectPeriod={onSelectPeriod}
            rows={[
              {
                label: "Tax Liabilities",
                field: "taxLiabilities",
                format: "currency",
              },
              {
                label: "Super Liabilities",
                field: "superannuationLiabilities",
                format: "currency",
              },
            ]}
            chart={
              canChart ? (
                <StatutoryLiabilitiesChart
                  periods={labels}
                  plotInsetLeft={METRIC_COLUMN_PX}
                  taxation={extractArray(periods, "taxLiabilities")}
                  superannuation={extractArray(
                    periods,
                    "superannuationLiabilities",
                  )}
                  other={periods.map(() => null)}
                />
              ) : null
            }
          />
        </>
      )}

      {/* Recorded vs ATO RBA (MOR-859) — the gap between what the company
          recorded and what the ATO says was owed is itself evidence, so both
          stay visible; the toggle only changes which one feeds the solvency
          indicators. */}
      {rbaBalanceAt && (
        <div className="border-t border-border">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-muted/30 px-5 py-2.5">
            <h5 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Tax liabilities — recorded vs ATO RBA
            </h5>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-muted-foreground">
                Drives solvency indicators:
              </span>
              <div className="flex items-center gap-0.5 rounded-md border border-border p-0.5">
                {(
                  [
                    { key: "financials", label: "Company financials" },
                    { key: "ato_rba", label: "ATO RBA" },
                  ] as const
                ).map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    disabled={!onSetStatutorySource}
                    onClick={() => onSetStatutorySource?.(key)}
                    className={cn(
                      "rounded px-2 py-0.5 text-[10px] transition-colors",
                      statutorySource === key
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground",
                      onSetStatutorySource
                        ? "cursor-pointer hover:bg-muted"
                        : "cursor-default opacity-70",
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <ScrollArea className="w-full">
            <table
              className="w-full table-fixed text-xs"
              style={{ minWidth: periodRegionMinWidth(periods.length) }}
            >
              <colgroup>
                <col style={{ width: METRIC_COLUMN_PX }} />
                {periods.map((p) => (
                  <col key={p._id} />
                ))}
              </colgroup>
              <tbody>
                {(
                  [
                    {
                      key: "recorded",
                      label: "Recorded (financials)",
                      active: statutorySource === "financials",
                      // When the RBA source is active the row's taxLiabilities
                      // is overlaid — recordedTaxLiabilities keeps the
                      // company's own figure visible for comparison.
                      value: (p: {
                        taxLiabilities?: number;
                        recordedTaxLiabilities?: number;
                        endDate?: string;
                      }) =>
                        p.recordedTaxLiabilities ?? p.taxLiabilities ?? null,
                    },
                    {
                      key: "rba",
                      label: "ATO RBA (actual)",
                      active: statutorySource === "ato_rba",
                      value: (p: {
                        taxLiabilities?: number;
                        recordedTaxLiabilities?: number;
                        endDate?: string;
                      }) => rbaBalanceAt(p.endDate),
                    },
                    {
                      key: "variance",
                      label: "Variance",
                      active: false,
                      value: (p: {
                        taxLiabilities?: number;
                        recordedTaxLiabilities?: number;
                        endDate?: string;
                      }) => {
                        const recorded =
                          p.recordedTaxLiabilities ?? p.taxLiabilities ?? null;
                        const rba = rbaBalanceAt(p.endDate);
                        return recorded != null && rba != null
                          ? rba - recorded
                          : null;
                      },
                    },
                  ] as const
                ).map((row) => (
                  <tr
                    key={row.key}
                    className={cn(
                      "border-b border-border/50 last:border-b-0",
                      row.active && "bg-primary/5",
                      row.key !== "variance" && !row.active && "opacity-60",
                    )}
                  >
                    <td className="px-4 py-2 text-xs text-foreground/80">
                      <span
                        className={cn(
                          "inline-flex items-center gap-1.5",
                          row.key === "variance" && "font-semibold",
                          row.active && "font-semibold",
                        )}
                      >
                        {row.label}
                        {row.active && (
                          <span className="shrink-0 rounded bg-primary/10 px-1 py-px text-[9px] font-medium text-primary">
                            Drives indicators
                          </span>
                        )}
                      </span>
                    </td>
                    {periods.map((p) => {
                      const val = row.value(p);
                      return (
                        <td
                          key={p._id}
                          onClick={
                            onSelectPeriod
                              ? () => onSelectPeriod(p._id)
                              : undefined
                          }
                          className={cn(
                            "px-3 py-2 text-center text-xs tabular-nums",
                            onSelectPeriod &&
                              "cursor-pointer hover:bg-muted/40",
                            val == null && "text-muted-foreground/40",
                            row.key === "variance" &&
                              val != null &&
                              Math.abs(val) > 0.5 &&
                              "font-semibold text-amber-600 dark:text-amber-400",
                          )}
                        >
                          {val == null ? "—" : formatValue(val, "currency")}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
          <p className="border-t border-border/50 px-5 py-2 text-[10px] text-muted-foreground">
            A positive variance means the ATO says more was owed than the
            company recorded — a books-and-records signal in its own right.
            Periods ending before the RBA's coverage show no RBA figure.
          </p>
        </div>
      )}

      {/* Fixed Assets + Related Party */}
      {hasAny(
        periods,
        "propertyPlantEquipment",
        "totalNonCurrentAssets",
        "relatedPartyReceivables",
      ) && (
        <BlockTable
          title="Non-current assets & related parties"
          periods={periods}
          onViewDocument={onViewDocument}
          onSelectPeriod={onSelectPeriod}
          rows={[
            {
              label: "PP&E",
              field: "propertyPlantEquipment",
              format: "currency",
            },
            {
              label: "Intangible Assets",
              field: "intangibleAssets",
              format: "currency",
            },
            {
              label: "Total Non-Current Assets",
              field: "totalNonCurrentAssets",
              format: "currency",
              isBold: true,
            },
            {
              label: "RP Receivables",
              field: "relatedPartyReceivables",
              format: "currency",
            },
          ]}
        />
      )}

      {/* Aged Payables */}
      {hasAny(periods, "payablesCurrent", "payables30Days") && (
        <>
          <BlockTable
            title="Aged payables"
            periods={periods}
            onViewDocument={onViewDocument}
            onSelectPeriod={onSelectPeriod}
            rows={[
              {
                label: "Current",
                field: "payablesCurrent",
                format: "currency",
              },
              { label: "30 Days", field: "payables30Days", format: "currency" },
              { label: "60 Days", field: "payables60Days", format: "currency" },
              {
                label: "90+ Days",
                field: "payables90PlusDays",
                format: "currency",
              },
            ]}
            chart={
              canChart ? (
                <AgedPayablesChart
                  periods={labels}
                  plotInsetLeft={METRIC_COLUMN_PX}
                  bucket0_30={extractArray(periods, "payablesCurrent")}
                  bucket31_60={extractArray(periods, "payables30Days")}
                  bucket61_90={extractArray(periods, "payables60Days")}
                  bucket90plus={extractArray(periods, "payables90PlusDays")}
                />
              ) : null
            }
          />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Block Table
// ---------------------------------------------------------------------------

interface BlockRow {
  label: string;
  field: string;
  format: "currency" | "ratio" | "percentage";
  isBold?: boolean;
  /** Inline helper copy rendered behind an info icon on the row label. */
  hint?: string;
}

interface FieldSource {
  documentId?: string;
  fileName?: string;
  lineItem?: string;
  value?: number;
  type: "stated" | "component" | "derived";
  formula?: string;
}

// ---------------------------------------------------------------------------
// Block Table — rows with component sources expand inline on click
// ---------------------------------------------------------------------------

/** Check if a row has expandable component sources in any period */
function hasComponentSources(
  periods: WorksheetPeriodEntry[],
  field: string,
): boolean {
  return periods.some((p) => {
    const sources: FieldSource[] | undefined = p.fieldSources?.[field];
    return sources?.some((s) => s.type === "component");
  });
}

/** Collect unique component line items across all periods for a field */
function collectComponentLineItems(
  periods: WorksheetPeriodEntry[],
  field: string,
): string[] {
  const items = new Set<string>();
  for (const p of periods) {
    const sources: FieldSource[] | undefined = p.fieldSources?.[field];
    if (!sources) continue;
    for (const s of sources) {
      if (s.type === "component" && s.lineItem) {
        items.add(s.lineItem);
      }
    }
  }
  return Array.from(items);
}

/** Get a component source value for a specific period and line item */
function getComponentValue(
  period: WorksheetPeriodEntry,
  field: string,
  lineItem: string,
): number | null {
  const sources: FieldSource[] | undefined = period.fieldSources?.[field];
  if (!sources) return null;
  const match = sources.find(
    (s) => s.type === "component" && s.lineItem === lineItem,
  );
  return match?.value ?? null;
}

/** Collect unique source documents across all component sources for a field */
function collectSourceDocs(
  periods: WorksheetPeriodEntry[],
  field: string,
): Array<{ documentId: string; fileName: string }> {
  const docs = new Map<string, { documentId: string; fileName: string }>();
  for (const p of periods) {
    const sources: FieldSource[] | undefined = p.fieldSources?.[field];
    if (!sources) continue;
    for (const s of sources) {
      if (s.documentId && s.fileName)
        docs.set(s.documentId, {
          documentId: s.documentId,
          fileName: s.fileName,
        });
    }
  }
  return Array.from(docs.values());
}

/**
 * A statement block as a period table: a fixed metric column, equal-width
 * period columns, and the optional `chart` sharing the same scroll region.
 */
function BlockTable({
  title,
  periods,
  rows,
  onViewDocument,
  onSelectPeriod,
  chart,
}: {
  title: string;
  periods: WorksheetPeriodEntry[];
  rows: BlockRow[];
  onViewDocument?: (docId: string) => void;
  onSelectPeriod?: (id: string) => void;
  /**
   * Chart drawn beneath the table inside the same horizontal scroll region
   * and minimum width, so its periods stay under their columns (MOR-1282).
   */
  chart?: React.ReactNode;
}) {
  const docTitle = useDocTitle();
  const highlightPeriodId = useContext(HighlightPeriodContext);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const visibleRows = rows.filter((row) =>
    periods.some((p) => p[row.field] != null),
  );
  if (visibleRows.length === 0) return null;

  function toggleRow(field: string) {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      return next;
    });
  }

  return (
    <div className="border-t border-border">
      <div className="border-b border-border bg-muted/30 px-5 py-2.5">
        <h5 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </h5>
      </div>
      <ScrollArea className="w-full">
        <table
          className="w-full table-fixed text-xs"
          style={{ minWidth: periodRegionMinWidth(periods.length) }}
        >
          {/* Metric column fixed; period columns split the rest equally —
              the geometry the paired chart reproduces (MOR-1282). */}
          <colgroup>
            <col style={{ width: METRIC_COLUMN_PX }} />
            {periods.map((p) => (
              <col key={p._id} />
            ))}
          </colgroup>
          <thead>
            <tr className="border-b border-border">
              <th className="px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Metric
              </th>
              {periods.map((p) => (
                <th
                  key={p._id}
                  aria-current={
                    p._id === highlightPeriodId ? "true" : undefined
                  }
                  className={cn(
                    "px-3 py-2 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground",
                    p._id === highlightPeriodId &&
                      "bg-violet-100/70 text-violet-800 dark:bg-violet-950/50 dark:text-violet-200",
                  )}
                >
                  {onSelectPeriod ? (
                    <button
                      type="button"
                      onClick={() => onSelectPeriod(p._id)}
                      className="cursor-pointer decoration-dotted transition-colors hover:text-foreground hover:underline"
                    >
                      {displayPeriodLabel(p)}
                    </button>
                  ) : (
                    displayPeriodLabel(p)
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => {
              const isExpandable = hasComponentSources(periods, row.field);
              const isExpanded = expandedRows.has(row.field);
              const allComponentItems = isExpandable
                ? collectComponentLineItems(periods, row.field)
                : [];
              const componentItems = isExpanded ? allComponentItems : [];
              const sourceDocs = isExpanded
                ? collectSourceDocs(periods, row.field)
                : [];

              return (
                <Fragment key={row.field}>
                  {/* Main row */}
                  <tr
                    className={cn(
                      "border-b border-border/50 transition-colors",
                      row.isBold && "bg-muted/20 font-semibold",
                      isExpandable && "cursor-pointer hover:bg-muted/30",
                      !isExpandable && "hover:bg-muted/20",
                      isExpanded && "bg-muted/25",
                    )}
                    onClick={
                      isExpandable ? () => toggleRow(row.field) : undefined
                    }
                  >
                    <td className="px-4 py-2.5 text-xs text-foreground/80">
                      <span className="flex items-center gap-1.5">
                        {isExpandable && (
                          <ChevronRight
                            className={cn(
                              "h-3 w-3 shrink-0 text-muted-foreground transition-transform duration-200",
                              isExpanded && "rotate-90",
                            )}
                          />
                        )}
                        {row.label}
                        {/* Merge-transparency: how many source lines were
                            amalgamated into this category (MOR-857). */}
                        {allComponentItems.length >= 2 && (
                          <span className="shrink-0 rounded bg-violet-100 px-1 py-px text-[9px] font-medium text-violet-600 dark:bg-violet-950 dark:text-violet-400">
                            {allComponentItems.length} lines
                          </span>
                        )}
                        {row.hint && (
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info
                                  className="h-3 w-3 shrink-0 cursor-help text-muted-foreground/60 hover:text-muted-foreground"
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs text-xs leading-relaxed">
                                {row.hint}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </span>
                    </td>
                    {periods.map((p) => {
                      const raw = p[row.field];
                      const val = typeof raw === "number" ? raw : undefined;
                      const sources: FieldSource[] =
                        p.fieldSources?.[row.field] ?? [];
                      const hasSources = sources.length > 0;
                      // MOR-810: this cell differs from the source figure
                      // because of a practitioner adjustment (adjusted basis).
                      const isAdjusted =
                        Array.isArray(p.adjustedFields) &&
                        p.adjustedFields.includes(row.field);
                      // MOR-859: this cell is driven by the ATO RBA ledger
                      // (practitioner-selected statutory source).
                      const isRbaDriven =
                        !isAdjusted &&
                        Array.isArray(p.rbaFields) &&
                        p.rbaFields.includes(row.field);

                      return (
                        <td
                          key={p._id}
                          // Any cell opens its period's detail panel — the
                          // column header should not be the only click target.
                          // Label-cell clicks still toggle source expansion.
                          onClick={
                            onSelectPeriod
                              ? (e) => {
                                  e.stopPropagation();
                                  onSelectPeriod(p._id);
                                }
                              : undefined
                          }
                          className={cn(
                            "px-3 py-2.5 text-center text-xs tabular-nums",
                            p._id === highlightPeriodId &&
                              "bg-violet-50/70 dark:bg-violet-950/30",
                            onSelectPeriod &&
                              "cursor-pointer hover:bg-muted/40",
                            val == null && "text-muted-foreground/40",
                            val != null &&
                              val < 0 &&
                              "text-red-600 dark:text-red-400",
                            val != null && val >= 0 && "text-foreground",
                            isAdjusted &&
                              "bg-amber-50/60 font-medium dark:bg-amber-950/20",
                            isRbaDriven &&
                              "bg-sky-50/60 font-medium dark:bg-sky-950/20",
                          )}
                          title={
                            isAdjusted
                              ? "Practitioner-adjusted figure — click to open the period"
                              : isRbaDriven
                                ? "Per the ATO RBA ledger (selected statutory source) — click to open the period"
                                : onSelectPeriod
                                  ? "Open period"
                                  : undefined
                          }
                        >
                          {val != null && hasSources ? (
                            <span className="inline-flex items-center gap-1">
                              {formatValue(val, row.format)}
                              {(() => {
                                const isDerived = sources.every(
                                  (s) => s.type === "derived",
                                );
                                const CellIcon = isDerived ? Calculator : Link2;
                                return (
                                  <CellIcon
                                    className={cn(
                                      "h-3 w-3 shrink-0",
                                      isDerived
                                        ? "text-amber-500/60 dark:text-amber-400/50"
                                        : "text-violet-500/60 dark:text-violet-400/50",
                                    )}
                                  />
                                );
                              })()}
                            </span>
                          ) : (
                            formatValue(val, row.format)
                          )}
                        </td>
                      );
                    })}
                  </tr>

                  {/* Expanded sub-rows */}
                  {isExpanded &&
                    componentItems.map((lineItem) => (
                      <tr
                        key={`${row.field}__${lineItem}`}
                        className="border-b border-border/30 bg-muted/10"
                      >
                        <td className="py-1.5 pl-10 pr-4 text-[11px] text-muted-foreground">
                          {lineItem}
                        </td>
                        {periods.map((p) => {
                          const val = getComponentValue(p, row.field, lineItem);
                          return (
                            <td
                              key={p._id}
                              className={cn(
                                "px-3 py-1.5 text-center text-[11px] tabular-nums",
                                p._id === highlightPeriodId &&
                                  "bg-violet-50/70 dark:bg-violet-950/30",
                                val == null && "text-muted-foreground/30",
                                val != null &&
                                  val < 0 &&
                                  "text-red-500/70 dark:text-red-400/70",
                                val != null && val >= 0 && "text-foreground/60",
                              )}
                            >
                              {val != null ? formatValue(val, row.format) : "—"}
                            </td>
                          );
                        })}
                      </tr>
                    ))}

                  {/* Source doc links row */}
                  {isExpanded && sourceDocs.length > 0 && (
                    <tr
                      key={`${row.field}__sources`}
                      className="border-b border-border/30 bg-muted/10"
                    >
                      <td
                        colSpan={1 + periods.length}
                        className="py-1.5 pl-10 pr-4"
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          {sourceDocs.map((doc) =>
                            onViewDocument ? (
                              <button
                                key={doc.documentId}
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onViewDocument(doc.documentId);
                                }}
                                className="inline-flex items-center gap-1 text-[10px] text-violet-600 hover:underline dark:text-violet-400"
                              >
                                <FileText className="h-3 w-3" />
                                {docTitle(doc)}
                              </button>
                            ) : (
                              <span
                                key={doc.documentId}
                                className="inline-flex items-center gap-1 text-[10px] text-muted-foreground"
                              >
                                <FileText className="h-3 w-3" />
                                {docTitle(doc)}
                              </span>
                            ),
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        {chart && (
          <div
            className="h-[220px] border-t border-border py-3 [&_.recharts-responsive-container]:!h-[190px] [&_[data-chart]]:!aspect-auto"
            style={{ minWidth: periodRegionMinWidth(periods.length) }}
          >
            {chart}
          </div>
        )}
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  );
}
